import * as THREE from "three";
import { loadRealWoodTexture } from "./textures";
import { fileOf, rankOf } from "../game/board";
import type { Square } from "../game/types";
import friezeUrl from "../assets/ui/frieze.webp";
import realWoodUrl from "../assets/ui/texture.webp";

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Echec de chargement de l'image : ${url}`));
    img.src = url;
  });
}

// Meme extrait (position + taille relative) que les cases (`GRAIN_OFFSETS[0]` /
// `repeat=0.3` plus bas) : le cadre doit montrer la meme veine de bois que
// l'echiquier, pas un grain different, pour rester coherent visuellement.
const FRAME_GRAIN_OFFSET: readonly [number, number] = [0.04, 0.06];
const FRAME_GRAIN_CROP_FRACTION = 0.3;

function drawTiledWoodGrain(ctx: CanvasRenderingContext2D, wood: HTMLImageElement, w: number, h: number, tiles: number): void {
  const cropSize = Math.min(wood.width, wood.height) * FRAME_GRAIN_CROP_FRACTION;
  const cropX = wood.width * FRAME_GRAIN_OFFSET[0];
  const cropY = wood.height * FRAME_GRAIN_OFFSET[1];
  const tileW = w / tiles;
  for (let i = 0; i < tiles; i++) {
    ctx.drawImage(wood, cropX, cropY, cropSize, cropSize, i * tileW, 0, tileW, h);
  }
}

const FRAME_TOP_W = 1024;
const FRAME_TOP_H = 220;
const FRAME_BAND_MARGIN = FRAME_TOP_H * 0.24;
const FRAME_BAND_H = FRAME_TOP_H - FRAME_BAND_MARGIN * 2;

/**
 * Compose un cote de cadre (dessus OU flanc exterieur, meme composition pour les
 * deux) : meme veine de bois que les cases (fond), repetee le long de la piece. La
 * frise celtique est inseree en bande centree, avec une marge de bois visible au-
 * dessus/en dessous plutot que plaquee bord a bord sur toute la largeur du cadre.
 * Un lisere sombre + une ombre portee sur les bords de la bande simulent une
 * incrustation (rainure gravee), en plus du relief physique donne par
 * `createFrameTopBumpTexture` ci-dessous. Utilisee pour le dessus ET le flanc
 * exterieur : c'est ce dernier qui est effectivement visible depuis la camera de
 * jeu (en plongee rasante), le dessus etant trop fin/trop en amont pour se voir —
 * la frise doit donc figurer sur les deux faces, pas seulement sur le dessus.
 */
function createFrameBandTexture(friezeTileCount: number): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = FRAME_TOP_W;
  canvas.height = FRAME_TOP_H;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // Sans filtrage anisotrope, cette texture (vue quasi de profil, en rasant, depuis
  // la camera de jeu) etait lissee/moyennee par le mipmapping au point de rendre la
  // frise gravee illisible malgre un contraste correct dans les pixels source (three.js
  // ecrete cette valeur a la limite reelle du GPU, un chiffre eleve est donc sans risque).
  texture.anisotropy = 16;

  void Promise.all([loadImage(realWoodUrl), loadImage(friezeUrl)]).then(([wood, frieze]) => {
    const ctx = canvas.getContext("2d")!;
    const { width: W, height: H } = canvas;
    const margin = FRAME_BAND_MARGIN;
    const bandH = FRAME_BAND_H;

    drawTiledWoodGrain(ctx, wood, W, H, Math.max(1, Math.round(friezeTileCount / 2)));

    // Respecte le ratio naturel de l'image source (~4.8:1, large et basse) plutot
    // que de l'ecraser dans des cases etroites determinees par `friezeTileCount` :
    // c'est cette distorsion (motif compresse horizontalement ~7x) qui le rendait
    // illisible, un moire quasi-aleatoire une fois minifie par le mipmapping, pas
    // un probleme de contraste. Chaque motif fait donc `bandH * ratio` de large,
    // repete autant de fois que necessaire pour couvrir le canvas.
    const tileDrawW = bandH * (frieze.width / frieze.height);
    const friezeTiles = Math.max(1, Math.round(W / tileDrawW));
    const actualTileW = W / friezeTiles;
    ctx.save();
    ctx.filter = "contrast(1.25) brightness(1.1)";
    for (let i = 0; i < friezeTiles; i++) {
      ctx.drawImage(frieze, i * actualTileW, margin, actualTileW, bandH);
    }
    ctx.restore();

    // Incrustation : ombre en creux sur les bords haut/bas de la bande, lisere net
    // a la jonction bois/frise, comme une rainure gravee dans laquelle la frise
    // serait encastree plutot que simplement collee dessus.
    const topShadow = ctx.createLinearGradient(0, margin, 0, margin + bandH * 0.14);
    topShadow.addColorStop(0, "rgba(0,0,0,0.6)");
    topShadow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = topShadow;
    ctx.fillRect(0, margin, W, bandH * 0.14);

    const bottomShadow = ctx.createLinearGradient(0, margin + bandH * 0.86, 0, margin + bandH);
    bottomShadow.addColorStop(0, "rgba(0,0,0,0)");
    bottomShadow.addColorStop(1, "rgba(0,0,0,0.5)");
    ctx.fillStyle = bottomShadow;
    ctx.fillRect(0, margin + bandH * 0.86, W, bandH * 0.14);

    ctx.strokeStyle = "rgba(0,0,0,0.7)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, margin);
    ctx.lineTo(W, margin);
    ctx.moveTo(0, margin + bandH);
    ctx.lineTo(W, margin + bandH);
    ctx.stroke();

    texture.needsUpdate = true;
  });

  return texture;
}

/**
 * Carte de relief (bumpMap) compagnon de `createFrameBandTexture` : bande centree
 * plus sombre (donc "en creux" sous eclairage) que le fond gris neutre, pour que
 * la frise reponde a la lumiere comme une vraie gravure incrustee plutot qu'une
 * simple image plaquee et plate.
 */
function createFrameTopBumpTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = FRAME_TOP_W;
  canvas.height = FRAME_TOP_H;
  const ctx = canvas.getContext("2d")!;
  const { width: W, height: H } = canvas;
  const margin = FRAME_BAND_MARGIN;
  const bandH = FRAME_BAND_H;

  ctx.fillStyle = "#808080";
  ctx.fillRect(0, 0, W, H);

  const groove = ctx.createLinearGradient(0, margin, 0, margin + bandH);
  groove.addColorStop(0, "#808080");
  groove.addColorStop(0.14, "#3a3a3a");
  groove.addColorStop(0.5, "#2c2c2c");
  groove.addColorStop(0.86, "#3a3a3a");
  groove.addColorStop(1, "#808080");
  ctx.fillStyle = groove;
  ctx.fillRect(0, margin, W, bandH);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 16;
  return texture;
}

/**
 * Carte emissive compagnon : silhouette de la frise seule (fond noir), pour une
 * legere lueur doree tracant la gravure. Le cadre passe le plus clair du temps
 * dans la penombre (loin des lumieres, posees haut au-dessus du plateau) ou le
 * relief physique seul (bump) ne suffit pas a le rendre lisible — un filet
 * "dore a la feuille" auto-eclaire, coherent avec une taverne aux chandelles,
 * le rend visible quel que soit l'eclairage ambiant a cet endroit.
 */
function createFrameBandEmissiveTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = FRAME_TOP_W;
  canvas.height = FRAME_TOP_H;
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 16;

  void loadImage(friezeUrl).then((frieze) => {
    const ctx = canvas.getContext("2d")!;
    const { width: W, height: H } = canvas;
    const margin = FRAME_BAND_MARGIN;
    const bandH = FRAME_BAND_H;

    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, W, H);

    const tileDrawW = bandH * (frieze.width / frieze.height);
    const friezeTiles = Math.max(1, Math.round(W / tileDrawW));
    const actualTileW = W / friezeTiles;
    ctx.save();
    ctx.filter = "contrast(1.4) brightness(0.85)";
    for (let i = 0; i < friezeTiles; i++) {
      ctx.drawImage(frieze, i * actualTileW, margin, actualTileW, bandH);
    }
    ctx.restore();

    texture.needsUpdate = true;
  });

  return texture;
}

export const SQUARE_SIZE = 1;
/** Hauteur (Y) du dessus de la table, en dehors du cadre : c'est la ou reposent
 * les pieces capturees (`capturedPieceRestPosition`), nettement plus bas que la
 * surface du plateau (Y=0) — cf. `SceneManager.setupTable`, seule autre
 * consommatrice de cette constante. */
export const TABLE_TOP_Y = -0.61;
const SQUARE_HEIGHT = 0.34;
const BOARD_SPAN = SQUARE_SIZE * 8;
const FRAME_WIDTH = 0.65;
const FRAME_HEIGHT = 0.62;
const FRAME_EDGE_RADIUS = 0.08;

/** Demi-largeur du plateau, cadre compris (bord exterieur) : utilise par la camera
 * pour garantir que le plateau tient entierement dans le champ, quel que soit le
 * ratio d'ecran (cf. `SceneManager.computeFov`). */
export const BOARD_OUTER_HALF_SPAN = BOARD_SPAN / 2 + FRAME_WIDTH;

/** Position monde (X/Z, Y=0 = surface du plateau) d'une case. Rang 0 (cote Blancs) vers Z negatif. */
export function squareToWorldPosition(square: Square): THREE.Vector3 {
  const file = fileOf(square);
  const rank = rankOf(square);
  return new THREE.Vector3((file - 3.5) * SQUARE_SIZE, 0, (rank - 3.5) * SQUARE_SIZE);
}

export interface BoardMeshes {
  readonly group: THREE.Group;
  readonly squareMeshes: THREE.Mesh[];
}

// Alignement "rail" hors plateau ou s'accumulent les pieces capturees : chaque
// camp a le sien, le long du bord exterieur du cadre sur SON cote (rang 0 pour
// les Blancs, rang 7 pour les Noirs), a l'ecart des chandelles (posees a x=+-6.4,
// z=0 — un rail le long de Z evite toute collision avec elles).
/** Exporte pour `SceneManager` (positionnement des lumieres dediees aux rails, cf.
 * `setupLighting`) : les pieces capturees y sont loin des lumieres du plateau et
 * ont besoin de leur propre eclairage pour rester lisibles (surtout les foncees). */
export const CAPTURE_RAIL_Z = BOARD_SPAN / 2 + FRAME_WIDTH + 0.85;
const CAPTURE_ROW_GAP = 0.45;
const CAPTURE_SLOT_SPACING = 0.5;
const CAPTURE_SLOTS_PER_ROW = 8;
const CAPTURE_ROW_HALF_WIDTH = ((CAPTURE_SLOTS_PER_ROW - 1) * CAPTURE_SLOT_SPACING) / 2;
// Souleve legerement les pieces capturees au-dessus de la surface de la table :
// sans ca, deux surfaces exactement coplanaires (le dessous de la piece et le
// dessus de la table, tous deux a TABLE_TOP_Y) scintillent (z-fighting).
const CAPTURE_TRAY_LIFT = 0.01;

/** Position monde de repos (debout, posee sur la table) pour la `index`-ieme piece
 * capturee de la couleur `capturedColor` : Y = `TABLE_TOP_Y` (+ leger souleve, cf.
 * `CAPTURE_TRAY_LIFT`) — pas la surface du plateau (Y=0). Le contraste avec les
 * pieces foncees sur le bois sombre de la table est assure par l'eclairage dedie
 * du rail (cf. `setupLighting` dans `SceneManager`), pas par un support visuel. */
export function capturedPieceRestPosition(capturedColor: "w" | "b", index: number): THREE.Vector3 {
  const row = Math.floor(index / CAPTURE_SLOTS_PER_ROW);
  const col = index % CAPTURE_SLOTS_PER_ROW;
  const z = (capturedColor === "w" ? -1 : 1) * (CAPTURE_RAIL_Z + row * CAPTURE_ROW_GAP);
  const x = -CAPTURE_ROW_HALF_WIDTH + col * CAPTURE_SLOT_SPACING;
  return new THREE.Vector3(x, TABLE_TOP_Y + CAPTURE_TRAY_LIFT, z);
}

export function buildBoard(): BoardMeshes {
  const group = new THREE.Group();
  group.name = "board";

  // Meme photo de grain de bois pour les deux tons de case, juste teintee differemment
  // (comme un echiquier reel taille dans deux essences claire/foncee du meme grain).
  // Extraits non repetes (repeat < 1) plutot qu'un carrelage : la photo source a un
  // fort reflet diagonal cuit dans l'image, qui se voyait comme un motif repete
  // disgracieux (repeat > 1) et se comportait comme un second eclairage "en conserve"
  // par-dessus l'eclairage reel de la scene (cases inegalement sombres). Plusieurs
  // extraits (tous pris dans la zone la plus uniforme de la photo, coin bas-gauche)
  // plutot qu'un seul : sinon les 32 cases claires sont pixel pour pixel identiques
  // entre elles (idem pour les foncees), ce qui se voit comme une repetition a
  // l'echelle du plateau meme sans raccord visible a l'interieur d'une case.
  const GRAIN_OFFSETS: readonly [number, number][] = [
    [0.04, 0.06],
    [0.22, 0.08],
    [0.4, 0.05],
    [0.06, 0.24],
    [0.24, 0.26],
    [0.42, 0.22],
  ];
  const lightMaterials = GRAIN_OFFSETS.map(
    ([ox, oy]) =>
      new THREE.MeshStandardMaterial({ map: loadRealWoodTexture(0.3, 0.3, ox, oy), color: "#f2cf8e", roughness: 0.75 }),
  );
  const darkMaterials = GRAIN_OFFSETS.map(
    ([ox, oy]) =>
      new THREE.MeshStandardMaterial({ map: loadRealWoodTexture(0.3, 0.3, ox, oy), color: "#6b4520", roughness: 0.78 }),
  );

  const squareGeometry = new THREE.BoxGeometry(SQUARE_SIZE * 0.98, SQUARE_HEIGHT, SQUARE_SIZE * 0.98);
  const squareMeshes: THREE.Mesh[] = [];

  for (let square = 0; square < 64; square++) {
    const file = fileOf(square);
    const rank = rankOf(square);
    const isLight = (file + rank) % 2 === 1;
    // Melange non aligne avec le damier (pas file+rank) pour que le choix de l'extrait
    // n'accentue pas lui-meme un motif visible.
    const variant = (file * 5 + rank * 3) % GRAIN_OFFSETS.length;
    const material = (isLight ? lightMaterials : darkMaterials)[variant]!;
    const mesh = new THREE.Mesh(squareGeometry, material);
    const pos = squareToWorldPosition(square);
    mesh.position.set(pos.x, -SQUARE_HEIGHT / 2, pos.z);
    mesh.receiveShadow = true;
    mesh.userData.square = square;
    mesh.name = `square-${square}`;
    group.add(mesh);
    squareMeshes.push(mesh);
  }

  // Cadre massif, epais, aux aretes arrondies (chene taille a la hache plutot que
  // planche debitee a angles vifs — esprit rustique/viking) : grain de bois reel sur
  // les flancs, frise celtique inseree avec marge de bois sur le dessus de chaque cote.
  // Chaque cote est un trapeze (bord interieur = cote du plateau, bord exterieur plus
  // large) : les 2 flancs obliques du trapeze sont exactement les onglets a 45 deg qui
  // viennent s'assembler avec ceux du cote voisin, comme un vrai cadre menuise.
  const outerSpan = BOARD_SPAN + FRAME_WIDTH * 2;
  const innerHalf = BOARD_SPAN / 2;
  const outerHalf = outerSpan / 2;

  const mitreShape = new THREE.Shape();
  mitreShape.moveTo(-innerHalf, innerHalf);
  mitreShape.lineTo(innerHalf, innerHalf);
  mitreShape.lineTo(outerHalf, outerHalf);
  mitreShape.lineTo(-outerHalf, outerHalf);
  mitreShape.lineTo(-innerHalf, innerHalf);

  const bevel = Math.min(FRAME_EDGE_RADIUS, FRAME_WIDTH / 3, FRAME_HEIGHT / 3);
  const frameGeometry = new THREE.ExtrudeGeometry(mitreShape, {
    depth: FRAME_HEIGHT,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 3,
    curveSegments: 1,
  });
  // Le plan du trapeze (dessine en XY) devient le sol XZ ; l'extrusion (0..FRAME_HEIGHT
  // en Z local) devient la hauteur Y, translatee pour que le dessus arrive a y=0.01
  // (aligne sur la surface des cases) et que le dessous morde legerement sous la table.
  frameGeometry.rotateX(-Math.PI / 2);
  frameGeometry.translate(0, -FRAME_HEIGHT + 0.01, 0);

  // Meme composition (bois + frise incrustee) sur le dessus ET le flanc exterieur :
  // teinte du flanc nettement eclaircie (elle etait bien plus sombre, "#4a2f18",
  // quand elle ne portait qu'un grain de bois nu — a cette teinte, la frise s'y
  // fondait dans l'ombre et devenait quasi invisible depuis la camera de jeu).
  const frameBandBump = createFrameTopBumpTexture();
  const frameBandEmissive = createFrameBandEmissiveTexture();
  const frameSideTexture = createFrameBandTexture(Math.round(outerSpan / 0.7));
  const frameSideMaterial = new THREE.MeshStandardMaterial({
    map: frameSideTexture,
    bumpMap: frameBandBump,
    bumpScale: 0.045,
    emissiveMap: frameBandEmissive,
    emissive: "#d1a25c",
    emissiveIntensity: 0.3,
    color: "#8a6238",
    roughness: 0.75,
  });
  const topTexture = createFrameBandTexture(Math.round(outerSpan / 0.7));
  const frameTopMaterial = new THREE.MeshStandardMaterial({
    map: topTexture,
    bumpMap: frameBandBump,
    bumpScale: 0.035,
    emissiveMap: frameBandEmissive,
    emissive: "#d1a25c",
    emissiveIntensity: 0.22,
    color: "#8a6238",
    roughness: 0.7,
  });
  // Groupes generes par ExtrudeGeometry pour une extrusion simple : 0 = capuchons
  // (dessus + dessous), 1 = flancs (parois laterales, dont les onglets obliques).
  const frameMaterials = [frameTopMaterial, frameSideMaterial];

  for (let i = 0; i < 4; i++) {
    const mesh = new THREE.Mesh(frameGeometry, frameMaterials);
    mesh.rotation.y = (i * Math.PI) / 2;
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    group.add(mesh);
  }

  return { group, squareMeshes };
}
